import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Tabs, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { GalleryIcon } from '@/Components/Common/Icons/GalleryIcon';
import GalleryMessages from '@/Components/Gallery/GalleryMessages';
import GalleryPhotos from '@/Components/Gallery/GalleryPhotos';
import GalleryVideos from '@/Components/Gallery/GalleryVideos';
import ResourceUploadModal from '@/Components/Gallery/ResourceUploadModal';
import { GalleryResourceTypes, UPLOAD_LIMITS } from '@/constants/galleryUpload';
import './Gallery.less';

const GALLERY_TAB_KEYS = {
  PHOTOS: GalleryResourceTypes.PHOTOS,
  VIDEOS: GalleryResourceTypes.VIDEOS,
  MESSAGES: GalleryResourceTypes.MESSAGES,
};

const PHOTO_FILE_EXTENSION_REGEX = /\.(jpg|jpeg|png|gif|bmp|webp|heic|heif)$/i;

const Gallery = ({ hubRecord, onUploadClick }) => {
  const trans = useSelector(translator);
  const [activeTab, setActiveTab] = useState(GALLERY_TAB_KEYS.PHOTOS);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadResourceType, setUploadResourceType] = useState(GALLERY_TAB_KEYS.PHOTOS);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState([]);
  const fileInputRef = useRef(null);
  const accountUuid = hubRecord?.useruuid;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
    if (typeof onUploadClick === 'function') {
      onUploadClick();
    }
  };

  const handleFileSelection = (event) => {
    const selectedList = Array.from(event.target.files || []);

    event.target.value = '';

    const isVideoTab = activeTab === GALLERY_TAB_KEYS.VIDEOS;
    const resourceType = isVideoTab ? GALLERY_TAB_KEYS.VIDEOS : GALLERY_TAB_KEYS.PHOTOS;
    const candidates = isVideoTab
      ? selectedList
      : selectedList.filter(
          (file) =>
            file.type?.startsWith('image/') || PHOTO_FILE_EXTENSION_REGEX.test(file.name || '')
        );

    if (candidates.length === 0) {
      return;
    }

    const { max, warningKey } = UPLOAD_LIMITS[resourceType];
    if (candidates.length > max) {
      message.warning(trans(warningKey).replaceAll('{{count}}', `${max}`));
    }
    setUploadResourceType(resourceType);
    setSelectedUploadFiles(candidates.slice(0, max));
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
    setSelectedUploadFiles([]);
  };

  const tabNavItems = [
    { key: GALLERY_TAB_KEYS.PHOTOS, label: trans('PHOTOS') },
    { key: GALLERY_TAB_KEYS.VIDEOS, label: trans('VIDEOS') },
    // Hiding the messages tab for now as per the requirement
    // { key: GALLERY_TAB_KEYS.MESSAGES, label: trans('MESSAGES') },
  ];

  return (
    <div className="gallery-container">
      <div className="gallery-sticky-bar">
        <div className="gallery-header-container">
          <div className="gallery-header-title-container">
            <div className="gallery-header-icon">
              <GalleryIcon />
            </div>
            <div className="gallery-header-title">{trans('GALLERY')}</div>
          </div>
          <Button
            type="primary"
            className="gallery-upload-button"
            icon={<UploadOutlined />}
            onClick={handleUploadClick}
            disabled={activeTab === GALLERY_TAB_KEYS.MESSAGES}
          >
            {trans('UPLOAD')}
          </Button>
        </div>
        <div className="gallery-tabs-container">
          <Tabs
            activeKey={activeTab}
            defaultActiveKey={GALLERY_TAB_KEYS.PHOTOS}
            items={tabNavItems}
            onChange={setActiveTab}
          />
        </div>
      </div>
      <div className="gallery-tab-content-container">
        {activeTab === GALLERY_TAB_KEYS.PHOTOS ? (
          <GalleryPhotos hubRecord={hubRecord} />
        ) : activeTab === GALLERY_TAB_KEYS.VIDEOS ? (
          <GalleryVideos hubRecord={hubRecord} />
        ) : (
          <GalleryMessages key={hubRecord?.useruuid} hubRecord={hubRecord} />
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={activeTab === GALLERY_TAB_KEYS.VIDEOS ? 'video/mp4,.mp4' : 'image/*'}
        multiple
        className="gallery-video-upload-hidden-input"
        onChange={handleFileSelection}
      />

      <ResourceUploadModal
        open={isUploadModalOpen}
        onClose={handleUploadModalClose}
        accountUuid={accountUuid}
        selectedFilesFromParent={selectedUploadFiles}
        resourceType={uploadResourceType}
      />
    </div>
  );
};

Gallery.propTypes = {
  hubRecord: PropTypes.object,
  onUploadClick: PropTypes.func,
};

export default Gallery;
